<template>
  <div class="p-4">
    <!-- 消息标题和内容 -->
    <div class="mb-6">
      <h2 class="text-xl font-bold mb-2">{{ message.title }}</h2>
      <div class="text-gray-500 text-sm mb-3">{{ formatDate(message.createdOn) }}</div>
      <div class="bg-gray-50 p-4 rounded-lg whitespace-pre-wrap">{{ message.content }}</div>
    </div>
    
    <!-- 订单详情 -->
    <div class="mb-6" v-if="orderDetails">
      <h3 class="text-lg font-semibold mb-3 border-b pb-2">订单详情</h3>
      <div class="grid grid-cols-2 gap-4">
        <div class="flex">
          <span class="text-gray-600 w-24">订单编号：</span>
          <span>{{ orderDetails.orderNo }}</span>
        </div>
        <div class="flex">
          <span class="text-gray-600 w-24">订单日期：</span>
          <span>{{ formatDate(orderDetails.orderDate || orderDetails.createdOn) }}</span>
        </div>
        <div class="flex">
          <span class="text-gray-600 w-24">{{ getCustomerLabel() }}：</span>
          <span>{{ getCustomerName() }}</span>
        </div>
        <div class="flex">
          <span class="text-gray-600 w-24">订单金额：</span>
          <span>{{ orderDetails.totalAmount || orderDetails.amount }} 元</span>
        </div>
      </div>
    </div>
    <!-- 附件列表 -->
    <div class="mb-6" v-if="attachments && attachments.length > 0">
      <h3 class="text-lg font-semibold mb-3 border-b pb-2">附件</h3>
      <div class="space-y-2">
        <div v-for="(attachment, index) in attachments" :key="index" class="flex items-center justify-between p-3 bg-gray-50 rounded-lg mb-2 hover:bg-gray-100">
          <div class="flex items-center space-x-2">
            <template v-if="isImageUrl(attachment.url)">
              <img :src="attachment.url" class="w-10 h-10 object-cover rounded cursor-pointer" @click="showImagePreview(attachment.url)" />
            </template>
            <template v-else>
              <i class="fas" :class="{
                'fa-file-image text-blue-500': attachment.fileType?.startsWith('image/'),
                'fa-file-pdf text-red-500': attachment.fileType === 'application/pdf',
                'fa-file-word text-blue-700': attachment.fileType?.includes('word'),
                'fa-file-excel text-green-600': attachment.fileType?.includes('excel') || attachment.fileType?.includes('spreadsheet'),
                'fa-file text-gray-400': !attachment.fileType?.startsWith('image/') && !attachment.fileType?.includes('pdf') && !attachment.fileType?.includes('word') && !attachment.fileType?.includes('excel')
              }"></i>
            </template>
            <span class="text-sm">{{ attachment.fileName }}</span>
            <span class="text-xs text-gray-500" v-if="attachment.size">({{ formatSize(attachment.size) }})</span>
          </div>
        
          <button @click="downloadAttachment(attachment)" class="text-blue-600 hover:text-blue-800 text-sm">
            <i class="fas fa-download mr-1"></i> 下载
          </button>
        </div>
      </div>
    </div>
    
    <!-- 审批按钮 -->
    <div class="flex justify-end space-x-4" v-if="message.needApproval">
      <button
        class="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
        @click="handleApprove"
      >
        通过
      </button>
      <button
        class="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
        @click="handleReject"
      >
        驳回
      </button>
    </div>
    
    <!-- 删除按钮 -->
    <div class="flex justify-end space-x-4 mt-4">
      <button
        class="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        @click="handleDelete"
      >
        <i class="fas fa-trash mr-2"></i>删除消息
      </button>
    </div>
  </div>
  
  <div v-if="imageViewerVisible" class="fixed inset-0 bg-black/80 z-[9999] flex items-center justify-center p-4">
  <img 
    :src="currentImageUrl"
    class="w-full h-full object-contain"
    @click.self="imageViewerVisible = false"
    @error="handleImageError"
  />
</div>

    <!-- 驳回理由对话框 -->
  <ComponentDialog v-model="showRejectDialog" title="驳回理由">
    <div class="p-4">
      <div class="mb-4">
        <label class="block text-sm font-medium text-gray-700 mb-2">请输入驳回理由</label>
        <textarea
          v-model="rejectReason"
          rows="4"
          class="w-full border rounded p-2"
          :class="{ 'border-red-500': rejectReasonError }"
        ></textarea>
        <div v-if="rejectReasonError" class="text-red-500 text-sm mt-1">
          {{ rejectReasonError }}
        </div>
      </div>
      
      <div class="flex justify-end space-x-4">
        <button
          class="px-4 py-2 border rounded hover:bg-gray-100"
          @click="cancelReject"
        >
          取消
        </button>
        <button
          class="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
          @click="submitReject"
        >
          确认
        </button>
      </div>
    </div>
  </ComponentDialog>
  
  <!-- 删除确认对话框 -->
  <ComponentDialog v-model="showDeleteDialog" title="确认删除">
    <div class="p-4">
      <div class="mb-4">
        <p class="text-gray-700">确定要删除这条消息吗？此操作不可撤销。</p>
      </div>
      
      <div class="flex justify-end space-x-4">
        <button
          class="px-4 py-2 border rounded hover:bg-gray-100"
          @click="cancelDelete"
        >
          取消
        </button>
        <button
          class="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
          @click="confirmDelete"
        >
          确认删除
        </button>
      </div>
    </div>
  </ComponentDialog>
</template>

<script setup>
import { ref, defineProps, defineEmits, onMounted, watch,nextTick } from 'vue';
import ComponentDialog from '@/components/ComponentDialog.vue';
import { financeApi, purchaseApi, salesApi, purchaseReturnApi, messagesApi } from '@/api/api';
import toast from '@/utils/toast';
const currentImageUrl = ref('');
const imageViewerVisible = ref(false);
const imageLoadError = ref(false);
const props = defineProps({
  message: {
    type: Object,
    default: () => ({})
  },
  visible: {
    type: Boolean,
    default: false
  }
});
// 显示图片预览
const showImagePreview=(url)=> {
  if (!url) return;
  currentImageUrl.value = url + '?t=' + Date.now(); // 避免缓存
  nextTick(() => {
    imageViewerVisible.value = true;
  });
  imageLoadError.value = false;
}

// 关闭图片预览
 
// 判断URL是否为图片
const isImageUrl = (url) => {
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp']
  return imageExtensions.some(ext => url.toLowerCase().endsWith(ext))
}

const emit = defineEmits(['update:visible', 'refresh']);

// 监听visible属性变化
watch(() => props.visible, (newVal) => {
  if (newVal) {
    fetchOrderDetails();
    fetchAttachments();
  }
});

// 订单详情和附件
const orderDetails = ref(null);
const attachments = ref([]);

// 驳回相关状态
const showRejectDialog = ref(false);
const rejectReason = ref('');
const rejectReasonError = ref('');

// 删除相关状态
const showDeleteDialog = ref(false);

// 格式化日期
// 格式化文件尺寸
const formatSize = (bytes) => {
  if (!bytes) return '0B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let unitIndex = 0;
  while (bytes >= 1024 && unitIndex < units.length - 1) {
    bytes /= 1024;
    unitIndex++;
  }
  return bytes.toFixed(unitIndex === 0 ? 0 : 1) + units[unitIndex];
};

// 格式化日期
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  });
};

// 获取客户标签
const getCustomerLabel = () => {
  if (!props.message) return '客户名称';
  
  switch(props.message.orderType) {
    case 0: // 销售单
    case 4: // 销售退货单
      return '客户名称';
    case 1: // 进货单
    case 3: // 进货退货单
      return '供应商';
    case 2: // 开票单
      return '客户名称';
    default:
      return '客户名称';
  }
};

// 获取客户名称
const getCustomerName = () => {
  if (!orderDetails.value) return '';
  
  return orderDetails.value.customerName || 
         orderDetails.value.supplierName || 
         orderDetails.value.companyName || 
         '';
};

// 获取订单详情
const fetchOrderDetails = async () => {
  if (!props.message || !props.message.orderId) {
    return;
  }
  
  try {
    let response;
    
    // XSD = 0 销售单
    if(props.message.orderType === 0){
      response = await salesApi.getSalesDetail(props.message.orderId);
    }
    // JHD = 1 进货单
    else if(props.message.orderType === 1){
      response = await purchaseApi.getPurchaseById(props.message.orderId);
    }
    // INV = 2 开票单
    else if(props.message.orderType === 2){
      response = await financeApi.getInvoiceById(props.message.orderId);
    }
    // JHTD = 3 进货退货单
    else if(props.message.orderType === 3){
      response = await purchaseReturnApi.getPurchaseReturnDetail(props.message.orderId);
    }
    // XSTD = 4 销售退货单
    else if(props.message.orderType === 4){
      response = await salesApi.getSaleReturnDetail(props.message.orderId);
    }
    
    console.log(response);
    if (response && response.success) {
      orderDetails.value = response.data;
      console.log("orderdetails", orderDetails.value);
    }
  } catch (error) {
    console.error('获取订单详情失败:', error);
  }
};

// 获取附件列表
const fetchAttachments = async () => {
  if (!props.message || !props.message.orderId) return;
  
  try {
      //console.log("props.message.attachments",props.message.attachmentUrl);
      attachments.value = props.message.attachmentUrl;
    
  } catch (error) {
    console.error('获取附件列表失败:', error);
  }
};

// 下载附件
const downloadAttachment = (attachment) => {
  if (!attachment || !attachment.fileUrl) return;
  
  // 创建下载链接
  const link = document.createElement('a');
  link.href = attachment.fileUrl;
  link.download = attachment.fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// 关闭详情弹窗
const handleClose = () => {
  emit('update:visible', false);
};

// 审批通过
const handleApprove = async () => {
  try {
    const auditData = {
      orderId: props.message.orderId,
      messageId: props.message.id,
      rejectReason: ""
    };
    
    // XSD = 0 销售单
    if(props.message.orderType === 0){
      const response = await salesApi.AuditOrder(props.message.orderId);
      if(response.success){
         await messagesApi.markMessageAsRead({"messageId": auditData.messageId});
        toast.success("审批成功");
      } 
    }
    // JHD = 1 进货单
    else if(props.message.orderType === 1){
      const response = await purchaseApi.AuditOrder(auditData);
      if(response.success){
         await messagesApi.markMessageAsRead({"messageId": auditData.messageId});
        toast.success("审批成功");
      } 
    }
    // INV = 2 开票单
    else if(props.message.orderType === 2){
      const response = await financeApi.AuditOrder(auditData);
      if(response.success){
         await messagesApi.markMessageAsRead({"messageId": auditData.messageId});
        toast.success("审批成功");
      } 
    }
    // JHTD = 3 进货退货单
    else if(props.message.orderType === 4){
      const response = await purchaseReturnApi.AuditOrder(auditData.orderId);
      if(response.success){
         await messagesApi.markMessageAsRead({"messageId": auditData.messageId});
        toast.success("审批成功");
      } 
    }
    // XSTD = 4 销售退货单
    else if(props.message.orderType === 3){
      const response = await salesApi.AuditSaleReturn(auditData.orderId);
      if(response.success){
         await messagesApi.markMessageAsRead({"messageId": auditData.messageId});
        toast.success("审批成功");
      } 
    }
    
    emit('refresh');
    handleClose();
  } catch (error) {
    toast.error('审批操作失败:'+ error);
    console.error('审批操作失败:', error);
  }
};

// 显示驳回对话框
const handleReject = () => {
  showRejectDialog.value = true;
  rejectReason.value = '审核驳回';
  rejectReasonError.value = '审核驳回';
};

// 提交驳回
const submitReject = async () => {
  if (!rejectReason.value.trim()) {
    rejectReasonError.value = '请输入驳回理由';
    return;
  }

  try {
    const rejectData = {
      orderId: props.message.orderId,
      messageId: props.message.id,
      rejectReason: rejectReason.value
    };
    
    // XSD = 0 销售单
    if(props.message.orderType === 0){
      const response = await salesApi.UnAuditOrder(rejectData.orderId);
      if(response.success){
         await messagesApi.markMessageAsRead({"messageId": rejectData.messageId});
        toast.success("操作成功");
      } 
    }
    // JHD = 1 进货单
    else if(props.message.orderType === 1){
      const response = await purchaseApi.UnAuditOrder(rejectData);
      if(response.success){
         await messagesApi.markMessageAsRead({"messageId": rejectData.messageId});
        toast.success("操作成功");
      } 
    }
    // INV = 2 开票单
    else if(props.message.orderType === 2){
      const response = await financeApi.UnAuditOrder(rejectData);
      if(response.success){
         await messagesApi.markMessageAsRead({"messageId": rejectData.messageId});
        toast.success("操作成功");
      } 
    }
    // JHTD = 3 进货退货单
    else if(props.message.orderType === 4){
      const response = await purchaseReturnApi.UnAuditOrder(rejectData);
      if(response.success){
         await messagesApi.markMessageAsRead({"messageId": rejectData.messageId});
        toast.success("操作成功");
      } 
    }
    // XSTD = 4 销售退货单
    else if(props.message.orderType === 3){
      const response = await salesApi.UnAuditSaleReturn(rejectData);
      if(response.success){
         await messagesApi.markMessageAsRead({"messageId": rejectData.messageId});
        toast.success("操作成功");
      } 
    }
    
    showRejectDialog.value = false;
    emit('refresh');
    handleClose();
  } catch (error) {
    toast.error('驳回操作失败:'+ error);
    console.error('驳回操作失败:', error);
  }
};

// 取消驳回
const cancelReject = () => {
  showRejectDialog.value = false;
  rejectReason.value = '';
  rejectReasonError.value = '';
};

// 显示删除确认对话框
const handleDelete = () => {
  showDeleteDialog.value = true;
};

// 确认删除消息
const confirmDelete = async () => {
  try {
    const response = await messagesApi.deleteMessage(props.message.id);
    if (response && response.success) {
      toast.success('消息删除成功');
      showDeleteDialog.value = false;
      emit('refresh');
      handleClose();
    } else {
      toast.error(response?.message || '删除失败');
    }
  } catch (error) {
    console.error('删除消息失败:', error);
    toast.error('删除消息失败');
  }
};

// 取消删除
const cancelDelete = () => {
  showDeleteDialog.value = false;
};

onMounted(() => {
  console.log("props",props); 
  if (props.visible) {
    fetchOrderDetails();
    fetchAttachments();
  }
});
</script>
